/**
 * The caching layer, in one import.
 *
 *     import { useCachedData, TTL, invalidate, CACHE } from '../../ui/cache';
 *
 * Three pieces:
 *   cacheStore           the engine (memory + opt-in localStorage, TTLs,
 *                        per-user scoping, invalidation)
 *   useCachedData        the stale-while-revalidate hook that replaces
 *                        app/useData
 *   CacheToggle          the navbar switch, and the SW registration
 */

import cacheStore, {
  TTL,
  MODE_SWR,
  MODE_LIVE,
  get,
  set,
  peek,
  remove,
  invalidate,
  invalidateAll,
  clear,
  sweep,
  stats,
  scope,
  subscribe,
  getMode,
  setMode,
  toggleMode,
  isCacheEnabled,
  isCacheable,
} from './cacheStore';

import useCachedData, { formatAge } from './useCachedData';
import CacheToggle from './CacheToggle';
import useNetworkStatus, { isOnline, subscribeNetwork, isNetworkError } from './useNetworkStatus';
import registerServiceWorker, {
  applyUpdate,
  unregisterServiceWorker,
  clearStaticCaches,
} from './registerServiceWorker';

export {
  cacheStore,
  TTL,
  MODE_SWR,
  MODE_LIVE,
  get,
  set,
  peek,
  remove,
  invalidate,
  invalidateAll,
  clear,
  sweep,
  stats,
  scope,
  subscribe,
  getMode,
  setMode,
  toggleMode,
  isCacheEnabled,
  isCacheable,
  useCachedData,
  formatAge,
  CacheToggle,
  useNetworkStatus,
  isOnline,
  subscribeNetwork,
  isNetworkError,
  registerServiceWorker,
  applyUpdate,
  unregisterServiceWorker,
  clearStaticCaches,
};

/**
 * =====================================================================
 * CACHE POLICY — per endpoint
 * =====================================================================
 *
 * The one rule behind every number: a stale value is acceptable only
 * when acting on it cannot cause a real-world mistake. The most
 * expensive requests (the 692-row directory, the overdue list) are the
 * ones people act on hardest, so they get the SHORTEST lives, not the
 * longest. That is the opposite of what a naive "cache the slow stuff"
 * instinct produces, and it is the whole point.
 *
 * `persist: true` is a second, stricter decision: it means "safe to
 * show instantly after a browser restart, possibly a day later". Only
 * school structure qualifies. Nothing about a person, a mark, money or
 * attendance is ever written to disk — see NEVER_PERSIST in cacheStore,
 * which enforces this even if a call site asks for it.
 *
 * ---------------------------------------------------------------------
 *  endpoint                         ttl              persist  why
 * ---------------------------------------------------------------------
 *  /grades                          STATIC (24h)     yes      Edited a
 *  /subjects                                                  few times
 *  /batches                                                   a YEAR.
 *  /academic-years                                            Pure
 *  /exam-types                                                reference.
 *  /price-profiles                                            Safe to
 *                                                             show a day
 *                                                             old; this
 *                                                             is what
 *                                                             makes the
 *                                                             app usable
 *                                                             offline.
 *
 *  /sections                        REFERENCE (1h)   yes      Changes a
 *  /teachers                                                  few times
 *  /transportation                                            per term.
 *  /users                                                     Picker
 *  /assignments                                               fodder;
 *                                                             a new
 *                                                             teacher
 *                                                             appearing
 *                                                             an hour
 *                                                             late costs
 *                                                             nothing.
 *
 *  /students                        ROSTER (5m)      NO       Edited by
 *  /students?include_inactive=1                               the office
 *  /families                                                  during the
 *  /enrollments                                               day. 5m +
 *  /students/:id                                              SWR means
 *                                                             the fresh
 *                                                             copy lands
 *                                                             a moment
 *                                                             after the
 *                                                             stale one
 *                                                             paints.
 *                                                             Memory-only
 *                                                             because of
 *                                                             size AND
 *                                                             because it
 *                                                             is personal
 *                                                             data.
 *
 *  /exams                           RESULTS (2m)     NO       Written in
 *  /exams/:id/results                                         bursts by
 *  /students/:id/report-card                                  one teacher
 *                                                             at a time;
 *                                                             a stale
 *                                                             mark is
 *                                                             visible and
 *                                                             checkable.
 *
 *  Overview's counters              SUMMARY (1m)     NO       Never acted
 *                                                             on
 *                                                             directly.
 *
 *  /students/directory              VOLATILE (20s)   NO       THE BIG
 *    (?date, ?include_inactive)                               ONE. Carries
 *                                                             today's
 *                                                             attendance
 *                                                             AND the fee
 *                                                             balance per
 *                                                             student, so
 *                                                             it inherits
 *                                                             the shortest
 *                                                             life of
 *                                                             anything it
 *                                                             contains.
 *                                                             ~1MB, never
 *                                                             persisted.
 *
 *  /sections/:id/roster             VOLATILE (20s)   NO       A register
 *                                                             being taken
 *                                                             changes by
 *                                                             the second.
 *                                                             Two teachers
 *                                                             on one
 *                                                             section must
 *                                                             not overwrite
 *                                                             each other
 *                                                             from a stale
 *                                                             base. Cached
 *                                                             only enough
 *                                                             to survive a
 *                                                             modal
 *                                                             reopen.
 *
 *  /payments                        VOLATILE (20s)   NO       MONEY.
 *  /payments/overdue                                          Telling a
 *  /payments/overdue?include_left=1                           parent a
 *  /students/:id/payment-summary                              balance
 *                                                             that was
 *                                                             settled an
 *                                                             hour ago is
 *                                                             a real-world
 *                                                             error with a
 *                                                             real-world
 *                                                             argument
 *                                                             attached.
 *
 *  /notifications                   VOLATILE (20s)   NO       An unread
 *                                                             count that
 *                                                             lies is
 *                                                             worse than
 *                                                             no count.
 *
 *  /auth/*                          NONE             NO       Refused by
 *  /students/:id/attendance                                   cacheStore
 *                                                             outright.
 *                                                             login-verify
 *                                                             burns a
 *                                                             code; there
 *                                                             is no such
 *                                                             thing as a
 *                                                             cached
 *                                                             replay of
 *                                                             it.
 * ---------------------------------------------------------------------
 *
 * NOTE ON THE NEW QUERY PARAMS. `include_inactive` and `include_left`
 * change the ROWS, not just the view, so they must be part of the cache
 * key or the archived-students toggle will show the previous answer.
 * useCachedData handles this automatically as long as the flag is in
 * the hook's `deps` array — which it has to be anyway, or the loader
 * would not re-run when it changes. Concretely:
 *
 *     const [includeInactive, setIncludeInactive] = useState(false);
 *     useCachedData(
 *       () => api.students({ include_inactive: includeInactive ? 1 : undefined }),
 *       [includeInactive],                       // <- in deps, so in the key
 *       { key: 'students', ttl: TTL.ROSTER }
 *     );
 *
 * giving two distinct entries, 'students:[false]' and 'students:[true]'.
 */

/**
 * Cache keys as constants, so a page and an invalidation cannot drift
 * apart via a typo. Substring matching in invalidate() means
 * `invalidate(CACHE.STUDENTS)` also clears 'students:[true]',
 * 'student-directory:...' and 'students-page:[]'.
 */
export const CACHE = {
  GRADES: 'grades',
  SUBJECTS: 'subjects',
  BATCHES: 'batches',
  ACADEMIC_YEARS: 'academic-years',
  EXAM_TYPES: 'exam-types',
  PRICE_PROFILES: 'price-profiles',
  SECTIONS: 'sections',
  TEACHERS: 'teachers',
  TRANSPORTATION: 'transportation',
  USERS: 'users',
  ASSIGNMENTS: 'assignments',
  STUDENTS: 'students',
  STUDENT_DIRECTORY: 'student-directory',
  FAMILIES: 'families',
  ENROLLMENTS: 'enrollments',
  EXAMS: 'exams',
  RESULTS: 'exam-results',
  OVERVIEW: 'overview',
  ROSTER: 'roster',
  PAYMENTS: 'payments',
  NOTIFICATIONS: 'notifications',
};

/**
 * WHAT A MUTATION INVALIDATES.
 *
 * This is the half of caching that goes wrong in production, so it is
 * written down rather than left to each page to remember. After a
 * successful write, call `invalidateFor('<action>')` and then `reload()`.
 *
 * The entries are deliberately generous. Over-invalidating costs one
 * extra request; under-invalidating shows a user the row they just
 * deleted and destroys their trust in the whole app.
 */
export const INVALIDATES = {
  // Saving a student changes the list, the directory (balances/attendance
  // are joined into it), the family rollup and the section headcounts.
  student: [CACHE.STUDENTS, CACHE.STUDENT_DIRECTORY, CACHE.FAMILIES, CACHE.SECTIONS, CACHE.OVERVIEW],
  transfer: [CACHE.STUDENTS, CACHE.STUDENT_DIRECTORY, CACHE.ENROLLMENTS, CACHE.SECTIONS, CACHE.OVERVIEW],
  enrollment: [CACHE.ENROLLMENTS, CACHE.STUDENTS, CACHE.STUDENT_DIRECTORY, CACHE.SECTIONS],
  family: [CACHE.FAMILIES, CACHE.STUDENTS, CACHE.STUDENT_DIRECTORY],
  teacher: [CACHE.TEACHERS, CACHE.ASSIGNMENTS, CACHE.SECTIONS, CACHE.OVERVIEW],
  section: [CACHE.SECTIONS, CACHE.STUDENTS, CACHE.ENROLLMENTS, CACHE.ASSIGNMENTS, CACHE.OVERVIEW],
  assignment: [CACHE.ASSIGNMENTS, CACHE.SECTIONS, CACHE.TEACHERS],
  grade: [CACHE.GRADES, CACHE.SECTIONS, CACHE.OVERVIEW],
  subject: [CACHE.SUBJECTS, CACHE.ASSIGNMENTS, CACHE.EXAMS],
  batch: [CACHE.BATCHES, CACHE.SECTIONS],
  // Attendance feeds the directory's "today" column and the summaries.
  attendance: [CACHE.ROSTER, CACHE.STUDENT_DIRECTORY, CACHE.OVERVIEW],
  exam: [CACHE.EXAMS, CACHE.RESULTS],
  // A result changes the exam's own aggregate too.
  result: [CACHE.RESULTS, CACHE.EXAMS],
  examType: [CACHE.EXAM_TYPES, CACHE.EXAMS],
  // A payment moves a balance, which the directory renders per row.
  payment: [CACHE.PAYMENTS, CACHE.STUDENT_DIRECTORY, CACHE.STUDENTS, CACHE.OVERVIEW],
  priceProfile: [CACHE.PRICE_PROFILES, CACHE.PAYMENTS, CACHE.STUDENT_DIRECTORY],
  transportation: [CACHE.TRANSPORTATION, CACHE.STUDENTS, CACHE.STUDENT_DIRECTORY],
  user: [CACHE.USERS],
  notification: [CACHE.NOTIFICATIONS],
};

/**
 * Invalidate everything a given kind of write touches.
 *
 *     await api.updateStudent(id, form);
 *     invalidateFor('student');
 *     reload();
 *
 * @param {keyof INVALIDATES} action
 * @returns {number} entries dropped
 */
export function invalidateFor(action) {
  const patterns = INVALIDATES[action];
  if (!patterns) {
    // A typo'd action must not silently cache-poison the app. Failing
    // loud in development and falling back to a full clear in production
    // is the safe direction to be wrong in.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[cache] unknown invalidation action "${action}" — clearing everything instead`);
    }
    clear();
    return -1;
  }
  return invalidateAll(patterns);
}

/**
 * Call on LOGIN and on LOGOUT. Both, not either.
 *
 * Logout alone is not enough: the API client calls session.clear() and
 * reloads on a 401, people close tabs, and a session can begin with no
 * logout ever having run. Clearing at the start of a session is the only
 * point that is guaranteed to happen, and it is what stops the second
 * person on a shared school machine seeing the first person's rows.
 */
export function resetCacheForSession() {
  clear();
}

export default cacheStore;
